'use client';

import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslations } from 'next-intl';
import { StarkySenseResult, StarkySenseSolution } from '@/app/[locale]/api/starky-sense/route';
import { usePWADetection } from '@/utils/hooks/usePWADetection';
import { useRtl } from '@/utils/hooks/useRTL';
import { Badge } from '../ui/badge';

interface StarkySenseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRevise: (selectedSolutions: StarkySenseSolution[]) => void;
  onIgnore: () => void;
  result: StarkySenseResult | null;
  targetLanguage: string;
  isReviseDisabled?: boolean;
}

export function StarkySenseDialog({
  isOpen,
  onClose,
  onRevise,
  onIgnore,
  result,
  targetLanguage,
  isReviseDisabled = false
}: StarkySenseDialogProps) {
  const [selectedSolutions, setSelectedSolutions] = useState<Map<string, StarkySenseSolution>>(new Map());
  const [openSelectId, setOpenSelectId] = useState<string | null>(null);
  const t = useTranslations('starkySenseDialog');
  const { isMobile } = usePWADetection();
  const { direction } = useRtl();

  // Reset selected solutions when dialog opens with new result
  useEffect(() => {
    if (isOpen && result) {
      const newSelections = new Map<string, StarkySenseSolution>();

      // Set default selections (first bestCandidate or first solution)
      result.problems.forEach((problem, index) => {
        const bestCandidate = problem.solutions.find(sol => sol.bestCandidate);
        const defaultSolution = bestCandidate || problem.solutions[0];
        if (defaultSolution) {
          newSelections.set(`problem-${index}`, defaultSolution);
        }
      });

      setSelectedSolutions(newSelections);
      setOpenSelectId(null); // Reset open select when dialog opens
    }
  }, [isOpen, result]);

  const handleSolutionChange = (problemIndex: number, solution: StarkySenseSolution | null) => {
    const newSelections = new Map(selectedSolutions);
    const problemKey = `problem-${problemIndex}`;

    if (solution) {
      newSelections.set(problemKey, solution);
    } else {
      newSelections.delete(problemKey);
    }

    setSelectedSolutions(newSelections);
  };

  const handleRevise = () => {
    const solutions = Array.from(selectedSolutions.values());
    onRevise(solutions);
    onClose();
  };

  const hasAtLeastOneSolution = selectedSolutions.size > 0;

  if (!result || result.problems.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="h-[100vh] lg:h-[85vh] max-h-[100dvh] lg:max-h-[90vh] w-[100dvw] lg:w-[85vw] max-w-[100dvw] lg:max-w-5xl flex flex-col p-0 lg:p-3 m-0 lg:m-auto"
        onOpenAutoFocus={isMobile ? (e) => e.preventDefault() : undefined}
      >
        <div className="p-4 pb-0">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className='text-start'>
              {t('title')}
            </DialogTitle>
            <DialogDescription className="text-start block sm:hidden">
              {t('descriptionSm', { targetLanguage })}
            </DialogDescription>
            <DialogDescription className="text-start hidden sm:block">
              {t('description', { targetLanguage })}
            </DialogDescription>
          </DialogHeader>
        </div>

        <ScrollArea className="flex-1 mt-4 px-4" dir={direction}>
          <div className="space-y-6 pb-4 lg:pb-6" dir={direction}>
            {result.problems.map((problem, index) => {
              const problemKey = `problem-${index}`;
              const selectedSolution = selectedSolutions.get(problemKey);

              return (
                <div key={index} className="space-y-3">
                  <div className="space-y-2">
                    <div className="text-sm leading-relaxed text-start" dir="auto">
                      <Badge className="mb-1 [margin-inline-end:0.25rem]"
                        variant={"secondary"}>
                        @{problem.subject}
                      </Badge>
                      {problem.description}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {/* <label className="text-sm font-medium">
                      {t('selectSolution')}:
                    </label> */}

                    <Select
                      value={selectedSolution?.description || "__no_solution__"}
                      open={openSelectId === `problem-${index}`}
                      onOpenChange={(open) => {
                        if (open) {
                          setOpenSelectId(`problem-${index}`);
                        } else if (openSelectId === `problem-${index}`) {
                          setOpenSelectId(null);
                        }
                      }}
                      onValueChange={(value) => {
                        if (value === "__no_solution__") {
                          handleSolutionChange(index, null);
                        } else {
                          const solution = problem.solutions.find(sol => sol.description === value);
                          handleSolutionChange(index, solution || null);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full text-start">
                        <SelectValue placeholder={t('chooseSolution')} />
                      </SelectTrigger>
                      <SelectContent className="text-start">
                        <SelectItem value="__no_solution__">
                          {t('noSolution')}
                        </SelectItem>
                        {problem.solutions.map((solution, solutionIndex) => (
                          <SelectItem key={solutionIndex} value={solution.description}>
                            {solution.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end gap-2 p-4 pt-0">
          <Button
            variant="outline"
            onClick={() => {
              onIgnore();
              onClose();
            }}
          >
            {t('ignore')}
          </Button>
          <Button
            onClick={handleRevise}
            disabled={!hasAtLeastOneSolution || isReviseDisabled}
          >
            {t('revise')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
